import { useEffect, useState, type FormEvent } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  Info,
  Loader2,
  Plus,
  RefreshCw,
  Save,
  Trash2,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuthStore } from '@/lib/auth/store';

import { ApiErrorBanner } from './ApiErrorBanner';
import { SegmentationsCard }      from './SegmentationsCard';
import { GapConfigurationsCard }  from './GapConfigurationsCard';
import { ReportConfigsCard }      from './ReportConfigsCard';
import { CubixApiError } from '@/lib/api/types';
import {
  useAlmBuckets,
  useAlmParameters,
  useUpdateAlmBuckets,
  useUpdateAlmParameters,
  useUpdateCurveAssignments,
} from './globalApi';
import type {
  AlmParameters,
  CurveAssignment,
  CurveSource,
  Interpolation,
  RateColumn,
  TenantBucket,
} from './globalTypes';
import { BASEL_DEFAULTS as DEFAULT_BUCKETS } from './globalTypes';

/**
 * /settings/alm-parameters — Phase 2.0.PARAMS / Phase 2.0.PARAMS.LCR.
 *
 * Read-only for non-admins; editable for ALM_ADMIN. The save button is
 * disabled when the user lacks ALM_ADMIN so the page is browseable
 * without surprising 403s.
 *
 * Sections:
 *   1. Engine behavior (rate column, interpolation, curve source, two-eyes)
 *   2. NIM placeholder rates
 *   3. LCR parameters       (V117 + V118 wholesale run-off rates)
 *   4. NSFR parameters      (V117)
 *   5. EVE shock calibration (V118)
 *   6. Duration parameters   (V118)
 *   7. Bucket grid             (V127 / Phase 3.5)
 *   8. Per-currency curve assignments
 */
export function AlmParametersPage() {
  const isAdmin = useAuthStore((s) => s.hasRole('ALM_ADMIN'));
  const params = useAlmParameters();
  const update = useUpdateAlmParameters();
  const updateCurves = useUpdateCurveAssignments();

  // Phase 3.5 — bucket config
  const bucketsQuery  = useAlmBuckets();
  const updateBuckets = useUpdateAlmBuckets();
  const [bucketRows, setBucketRows] = useState<TenantBucket[]>(DEFAULT_BUCKETS);
  useEffect(() => {
    if (bucketsQuery.data?.buckets) setBucketRows(bucketsQuery.data.buckets);
  }, [bucketsQuery.data]);

  const [form, setForm] = useState<AlmParameters | null>(null);
  useEffect(() => {
    if (params.data) setForm(params.data);
  }, [params.data]);

  if (params.isLoading || !form) {
    return (
      <div className="p-8 text-muted-foreground">
        <Loader2 className="mr-2 inline h-4 w-4 animate-spin" /> Loading global parameters…
      </div>
    );
  }
  if (params.isError) return <ApiErrorBanner error={params.error} />;

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    update.mutate(form!);
  }

  function handleSaveCurves() {
    updateCurves.mutate(form!.curveAssignments);
  }

  function patch(p: Partial<AlmParameters>) {
    setForm({ ...form!, ...p });
  }

  function patchAssignment(idx: number, p: Partial<CurveAssignment>) {
    const next = [...form!.curveAssignments];
    next[idx] = { ...next[idx], ...p };
    patch({ curveAssignments: next });
  }

  function addAssignment() {
    patch({ curveAssignments: [...form!.curveAssignments, { currency: '', curveId: '' }] });
  }

  function removeAssignment(idx: number) {
    patch({ curveAssignments: form!.curveAssignments.filter((_, i) => i !== idx) });
  }

  return (
    <div className="space-y-6">
      <header className="flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Global ALM Parameters</h1>
          <p className="text-sm text-muted-foreground">
            Tenant-scoped engine configuration consumed by NIM, Duration, IRRBB, LCR/NSFR.
            See spec §5.5 for the full definition.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => params.refetch()}
          disabled={params.isFetching}
        >
          {params.isFetching ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="mr-2 h-4 w-4" />
          )}
          Reload
        </Button>
      </header>

      {!isAdmin ? (
        <div className="flex items-start gap-2 rounded-md border border-muted bg-muted/30 p-3 text-sm text-muted-foreground">
          <Info className="mt-0.5 h-4 w-4 shrink-0" />
          <p>You're viewing parameters in read-only mode. ALM_ADMIN role is required to edit.</p>
        </div>
      ) : null}

      <form onSubmit={handleSubmit} className="space-y-4">

        {/* ── 1. Engine behavior ─────────────────────────────────────────── */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Engine behavior</CardTitle>
            <CardDescription>
              These settings drive every rate-sensitive engine. Changes apply to the
              <em> next</em> calculation; existing results are not retroactively recomputed.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-3 md:grid-cols-3">
              <Field label="Rate column" htmlFor="p-rate-col">
                <select
                  id="p-rate-col"
                  value={form.rateColumn}
                  disabled={!isAdmin}
                  onChange={(e) => patch({ rateColumn: e.target.value as RateColumn })}
                  className={selectCls}
                >
                  <option value="ZERO">ZERO (bootstrapped, default)</option>
                  <option value="QUOTED">QUOTED (par-rate)</option>
                </select>
              </Field>
              <Field label="Tenor interpolation" htmlFor="p-interp">
                <select
                  id="p-interp"
                  value={form.interpolation}
                  disabled={!isAdmin}
                  onChange={(e) => patch({ interpolation: e.target.value as Interpolation })}
                  className={selectCls}
                >
                  <option value="LINEAR">LINEAR (default)</option>
                  <option value="CUBIC_SPLINE">CUBIC_SPLINE</option>
                  <option value="CLOSEST">CLOSEST</option>
                </select>
              </Field>
              <Field label="Curve source" htmlFor="p-source">
                <select
                  id="p-source"
                  value={form.curveSource}
                  disabled={!isAdmin}
                  onChange={(e) => patch({ curveSource: e.target.value as CurveSource })}
                  className={selectCls}
                >
                  <option value="UPLOAD">UPLOAD (REST + ALM validation)</option>
                  <option value="CX_MARKET">CX_MARKET (live feed — when ready)</option>
                </select>
              </Field>
            </div>
            <div className="mt-3 flex items-center gap-2 rounded-md border bg-muted/20 p-3">
              <input
                type="checkbox"
                id="p-two-eyes"
                checked={form.requireTwoEyesCurveValidation}
                disabled={!isAdmin}
                onChange={(e) => patch({ requireTwoEyesCurveValidation: e.target.checked })}
                className="h-4 w-4"
              />
              <Label htmlFor="p-two-eyes" className="cursor-pointer text-sm font-normal">
                <strong>Two-eyes validation for yield-curve snapshots.</strong>{' '}
                When enabled, a curve uploader cannot validate their own snapshot — a
                second ALM_ADMIN must sign it off before engines pick it up.
              </Label>
            </div>
          </CardContent>
        </Card>

        {/* ── 2. NIM placeholder rates ───────────────────────────────────── */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">NIM placeholder rates</CardTitle>
            <CardDescription>
              Used by the NIM engine when no validated yield-curve snapshot is available
              for the calculation date. Once the curve store is live, these become a
              degraded-mode fallback only.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-3 md:grid-cols-3">
              <Field label="Baseline asset rate (decimal)" htmlFor="p-asset">
                <Input
                  id="p-asset"
                  type="number"
                  step="0.000001"
                  min={0}
                  max={1}
                  disabled={!isAdmin}
                  value={form.nimBaselineAssetRate}
                  onChange={(e) => patch({ nimBaselineAssetRate: e.target.value })}
                />
              </Field>
              <Field label="Baseline liability rate (decimal)" htmlFor="p-liab">
                <Input
                  id="p-liab"
                  type="number"
                  step="0.000001"
                  min={0}
                  max={1}
                  disabled={!isAdmin}
                  value={form.nimBaselineLiabilityRate}
                  onChange={(e) => patch({ nimBaselineLiabilityRate: e.target.value })}
                />
              </Field>
              <Field label="Default shock (bp)" htmlFor="p-shock">
                <Input
                  id="p-shock"
                  type="number"
                  step="1"
                  min={0}
                  max={5000}
                  disabled={!isAdmin}
                  value={form.nimDefaultShockBp}
                  onChange={(e) => patch({ nimDefaultShockBp: Number(e.target.value) || 0 })}
                />
              </Field>
            </div>
          </CardContent>
        </Card>

        {/* ── 3. LCR parameters ─────────────────────────────────────────── */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">LCR parameters</CardTitle>
            <CardDescription>
              Basel III Liquidity Coverage Ratio calibration (BCBS §42, §75–§80).
              Defaults are the standard Basel III values. BAM may require a supervisory
              buffer above the 100% floor.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">

            {/* Thresholds & horizon */}
            <div>
              <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Compliance threshold &amp; horizon
              </p>
              <div className="grid gap-3 md:grid-cols-2">
                <Field label="Minimum LCR ratio (e.g. 1.00 = 100%)" htmlFor="p-lcr-threshold">
                  <Input
                    id="p-lcr-threshold"
                    type="number"
                    step="0.0001"
                    min={0.5}
                    max={2}
                    disabled={!isAdmin}
                    value={form.lcrMinThreshold}
                    onChange={(e) => patch({ lcrMinThreshold: e.target.value })}
                  />
                </Field>
                <Field label="Stress horizon (days, Basel: 30)" htmlFor="p-lcr-horizon">
                  <Input
                    id="p-lcr-horizon"
                    type="number"
                    step="1"
                    min={1}
                    max={365}
                    disabled={!isAdmin}
                    value={form.lcrStressHorizonDays}
                    onChange={(e) => patch({ lcrStressHorizonDays: Number(e.target.value) || 30 })}
                  />
                </Field>
              </div>
            </div>

            {/* HQLA haircuts */}
            <div>
              <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                HQLA haircuts (applied before summing)
              </p>
              <div className="grid gap-3 md:grid-cols-2">
                <Field label="Level 2A haircut (Basel: 0.15 = 15%)" htmlFor="p-l2a-haircut">
                  <Input
                    id="p-l2a-haircut"
                    type="number"
                    step="0.0001"
                    min={0}
                    max={1}
                    disabled={!isAdmin}
                    value={form.lcrHqlaLevel2aHaircut}
                    onChange={(e) => patch({ lcrHqlaLevel2aHaircut: e.target.value })}
                  />
                </Field>
                <Field label="Level 2B haircut (Basel: 0.50 = 50%)" htmlFor="p-l2b-haircut">
                  <Input
                    id="p-l2b-haircut"
                    type="number"
                    step="0.0001"
                    min={0}
                    max={1}
                    disabled={!isAdmin}
                    value={form.lcrHqlaLevel2bHaircut}
                    onChange={(e) => patch({ lcrHqlaLevel2bHaircut: e.target.value })}
                  />
                </Field>
              </div>
            </div>

            {/* HQLA concentration caps */}
            <div>
              <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                HQLA concentration caps (fraction of total HQLA post-haircut)
              </p>
              <div className="grid gap-3 md:grid-cols-2">
                <Field label="Level 2A cap (Basel: 0.40 = 40%)" htmlFor="p-l2a-cap">
                  <Input
                    id="p-l2a-cap"
                    type="number"
                    step="0.0001"
                    min={0}
                    max={1}
                    disabled={!isAdmin}
                    value={form.lcrLevel2aCapPct}
                    onChange={(e) => patch({ lcrLevel2aCapPct: e.target.value })}
                  />
                </Field>
                <Field label="Level 2B cap (Basel: 0.15 = 15%)" htmlFor="p-l2b-cap">
                  <Input
                    id="p-l2b-cap"
                    type="number"
                    step="0.0001"
                    min={0}
                    max={1}
                    disabled={!isAdmin}
                    value={form.lcrLevel2bCapPct}
                    onChange={(e) => patch({ lcrLevel2bCapPct: e.target.value })}
                  />
                </Field>
              </div>
            </div>

            {/* Retail run-off rates */}
            <div>
              <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Retail deposit run-off rates (BCBS §75–§80)
              </p>
              <div className="grid gap-3 md:grid-cols-2">
                <Field
                  label="Stable deposits run-off (Basel: 0.05 = 5%)"
                  htmlFor="p-runoff-stable"
                >
                  <Input
                    id="p-runoff-stable"
                    type="number"
                    step="0.0001"
                    min={0}
                    max={1}
                    disabled={!isAdmin}
                    value={form.lcrRunoffRetailStable}
                    onChange={(e) => patch({ lcrRunoffRetailStable: e.target.value })}
                  />
                </Field>
                <Field
                  label="Less-stable deposits run-off (Basel: 0.10 = 10%)"
                  htmlFor="p-runoff-less-stable"
                >
                  <Input
                    id="p-runoff-less-stable"
                    type="number"
                    step="0.0001"
                    min={0}
                    max={1}
                    disabled={!isAdmin}
                    value={form.lcrRunoffRetailLessStable}
                    onChange={(e) => patch({ lcrRunoffRetailLessStable: e.target.value })}
                  />
                </Field>
              </div>
            </div>

            {/* Wholesale run-off rates (V118) */}
            <div>
              <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Wholesale deposit run-off rates (BCBS §94–§96) — added V118
              </p>
              <div className="grid gap-3 md:grid-cols-2">
                <Field
                  label="Operational wholesale (Basel §94: 0.25 = 25%)"
                  htmlFor="p-runoff-ws-op"
                >
                  <Input
                    id="p-runoff-ws-op"
                    type="number"
                    step="0.0001"
                    min={0}
                    max={1}
                    disabled={!isAdmin}
                    value={form.lcrRunoffWholesaleOperational}
                    onChange={(e) => patch({ lcrRunoffWholesaleOperational: e.target.value })}
                  />
                </Field>
                <Field
                  label="Non-operational wholesale (Basel §96: 1.00 = 100%)"
                  htmlFor="p-runoff-ws-nonop"
                >
                  <Input
                    id="p-runoff-ws-nonop"
                    type="number"
                    step="0.0001"
                    min={0}
                    max={1}
                    disabled={!isAdmin}
                    value={form.lcrRunoffWholesaleNonOperational}
                    onChange={(e) => patch({ lcrRunoffWholesaleNonOperational: e.target.value })}
                  />
                </Field>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* ── 4. NSFR parameters ────────────────────────────────────────── */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">NSFR parameters</CardTitle>
            <CardDescription>
              Basel III Net Stable Funding Ratio calibration (BCBS 295).
              Per-instrument ASF/RSF factors are managed in the{' '}
              <code>gold.nsfr_factors</code> table (V116) and are not exposed
              here — this section covers the compliance threshold only.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-3 md:grid-cols-2">
              <Field label="Minimum NSFR ratio (e.g. 1.00 = 100%)" htmlFor="p-nsfr-threshold">
                <Input
                  id="p-nsfr-threshold"
                  type="number"
                  step="0.0001"
                  min={0.5}
                  max={2}
                  disabled={!isAdmin}
                  value={form.nsfrMinThreshold}
                  onChange={(e) => patch({ nsfrMinThreshold: e.target.value })}
                />
              </Field>
            </div>
            <p className="mt-3 text-xs text-muted-foreground">
              Per-instrument ASF/RSF weights (BCBS 295 Tables 1–2) are stored in{' '}
              <code>gold.nsfr_factors</code> and updated via Flyway migrations.
              Contact BAM compliance team before modifying.
            </p>
          </CardContent>
        </Card>

        {/* ── 5. EVE shock calibration (V118) ──────────────────────────── */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">EVE scenario shock calibration</CardTitle>
            <CardDescription>
              BCBS 368 "Interest Rate Risk in the Banking Book" (Apr 2016) Table 2.
              Shock magnitudes applied to the yield curve for each of the six prescribed
              IRRBB scenarios. Defaults are the Basel III standard values — only modify
              under BAM supervisory guidance.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">

            {/* Shock magnitudes */}
            <div>
              <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Scenario shock magnitudes
              </p>
              <div className="grid gap-3 md:grid-cols-3">
                <Field label="Parallel shock (bp, Basel: 200)" htmlFor="p-eve-parallel">
                  <Input
                    id="p-eve-parallel"
                    type="number"
                    step="1"
                    min={0}
                    max={5000}
                    disabled={!isAdmin}
                    value={form.eveParallelShockBp}
                    onChange={(e) => patch({ eveParallelShockBp: Number(e.target.value) || 0 })}
                  />
                </Field>
                <Field label="Short-rate shock at short end (bp, Basel: 250)" htmlFor="p-eve-short">
                  <Input
                    id="p-eve-short"
                    type="number"
                    step="1"
                    min={0}
                    max={5000}
                    disabled={!isAdmin}
                    value={form.eveShortRateShockBp}
                    onChange={(e) => patch({ eveShortRateShockBp: Number(e.target.value) || 0 })}
                  />
                </Field>
                <Field
                  label="Steepener/flattener half-swing (bp, Basel: 75)"
                  htmlFor="p-eve-rotation"
                >
                  <Input
                    id="p-eve-rotation"
                    type="number"
                    step="1"
                    min={0}
                    max={5000}
                    disabled={!isAdmin}
                    value={form.eveRotationShockBp}
                    onChange={(e) => patch({ eveRotationShockBp: Number(e.target.value) || 0 })}
                  />
                </Field>
              </div>
            </div>

            {/* Decay parameters */}
            <div>
              <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Short-rate decay &amp; ramp parameters
              </p>
              <div className="grid gap-3 md:grid-cols-2">
                <Field
                  label="Decay λ — short-rate half-life (Basel: 0.1733 = ln(2)/4)"
                  htmlFor="p-eve-lambda"
                >
                  <Input
                    id="p-eve-lambda"
                    type="number"
                    step="0.000000001"
                    min={0.0001}
                    max={1}
                    disabled={!isAdmin}
                    value={form.eveLambda}
                    onChange={(e) => patch({ eveLambda: e.target.value })}
                  />
                </Field>
                <Field
                  label="Max tenor for linear ramp (years, Basel: 30)"
                  htmlFor="p-eve-max-tenor"
                >
                  <Input
                    id="p-eve-max-tenor"
                    type="number"
                    step="1"
                    min={1}
                    max={100}
                    disabled={!isAdmin}
                    value={form.eveMaxTenorYears}
                    onChange={(e) => patch({ eveMaxTenorYears: Number(e.target.value) || 30 })}
                  />
                </Field>
              </div>
              <p className="mt-2 text-xs text-muted-foreground">
                λ controls how fast the short-rate shock decays toward zero at long tenors.
                The default ln(2)/4 ≈ 0.1733 gives a half-life of ≈ 4 years, matching BCBS 368
                exactly. Increase λ to make the shock decay faster; decrease to extend it.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* ── 6. Duration default shock (V118) ─────────────────────────── */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Duration engine parameters</CardTitle>
            <CardDescription>
              Default scenario used when the Duration calculation is triggered without an
              explicit RateScenario. Separated from NIM's shock so each engine can be
              calibrated independently.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-3 md:grid-cols-2">
              <Field
                label="Default parallel shock (bp, Basel / Default: 200)"
                htmlFor="p-dur-shock"
              >
                <Input
                  id="p-dur-shock"
                  type="number"
                  step="1"
                  min={0}
                  max={5000}
                  disabled={!isAdmin}
                  value={form.durationDefaultShockBp}
                  onChange={(e) => patch({ durationDefaultShockBp: Number(e.target.value) || 0 })}
                />
              </Field>
            </div>
            <p className="mt-2 text-xs text-muted-foreground">
              Applies a uniform parallel shift of this many basis points to the yield curve
              when computing stressed Modified Duration and DV01 with no explicit scenario
              provided (e.g. scheduled runs). Explicit scenario calls override this value.
            </p>
          </CardContent>
        </Card>

        {/* ── FTP v2 client rates ──────────────────────────────────────────── */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">FTP Client Rates (v2)</CardTitle>
            <CardDescription>
              Average client rate charged / paid per tenor bucket (decimal, e.g. 0.06 = 6%).
              The engine computes <strong>Spread = Client Rate − Avg FTP Rate</strong> for each
              bucket. Leave as 0 for buckets not yet calibrated.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
              {(
                [
                  ['ftpClientRate0to1M',  '0–1M'],
                  ['ftpClientRate1to3M',  '1–3M'],
                  ['ftpClientRate3to6M',  '3–6M'],
                  ['ftpClientRate6to12M', '6–12M'],
                  ['ftpClientRate1to2Y',  '1–2Y'],
                  ['ftpClientRate2to5Y',  '2–5Y'],
                  ['ftpClientRate5yPlus', '5Y+'],
                ] as [keyof typeof form, string][]
              ).map(([field, label]) => (
                <Field key={field} label={`Client rate ${label}`} htmlFor={`ftp-cr-${field}`}>
                  <Input
                    id={`ftp-cr-${field}`}
                    type="number"
                    step="0.001"
                    min={0}
                    max={1}
                    disabled={!isAdmin}
                    value={form[field] as string}
                    onChange={(e) => patch({ [field]: e.target.value })}
                  />
                </Field>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* ── IRRBB alert thresholds (Phase 3.4 / V126) ───────────────────── */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">IRRBB Alert Thresholds</CardTitle>
            <CardDescription>
              Thresholds used by the nightly batch to raise{' '}
              <strong>EVE_BREACH</strong> and <strong>NIM_BREACH</strong> alerts.
              Expressed as fractions (e.g. 0.15 = 15%). Changing these values takes
              effect on the next batch run (tonight at 23:15 or via{' '}
              <em>Run batch now</em> on the Alerts page).
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field
                label="EVE outlier threshold (BCBS 368 §99)"
                htmlFor="p-eve-outlier"
                hint="Alert when |worstCaseEve|/portfolioPv exceeds this value. Basel III default: 0.15 (15%)."
              >
                <Input
                  id="p-eve-outlier"
                  type="number"
                  step="0.01"
                  min={0.05}
                  max={0.50}
                  disabled={!isAdmin}
                  value={form.eveOutlierThresholdPct}
                  onChange={(e) => patch({ eveOutlierThresholdPct: e.target.value })}
                />
              </Field>
              <Field
                label="NIM stress-drop alert threshold"
                htmlFor="p-nim-drop"
                hint="Alert when (stressed NIM − baseline NIM) / baseline NIM falls below this fraction. Internal default: 0.20 (20%)."
              >
                <Input
                  id="p-nim-drop"
                  type="number"
                  step="0.01"
                  min={0.05}
                  max={0.80}
                  disabled={!isAdmin}
                  value={form.nimDropAlertThresholdPct}
                  onChange={(e) => patch({ nimDropAlertThresholdPct: e.target.value })}
                />
              </Field>
            </div>
          </CardContent>
        </Card>

        {update.isError ? <ApiErrorBanner error={update.error} /> : null}
        {update.isSuccess ? (
          <p className="flex items-center gap-1.5 text-sm text-emerald-600">
            <CheckCircle2 className="h-4 w-4" /> Parameters saved.
          </p>
        ) : null}

        <div className="flex items-center gap-2">
          <Button type="submit" disabled={!isAdmin || update.isPending}>
            {update.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Save className="mr-2 h-4 w-4" />
            )}
            Save parameters
          </Button>
        </div>
      </form>

      {/* ── 7. Bucket grid (Phase 3.5 / V127) ────────────────────────────── */}
      <BucketGridCard
        isAdmin={isAdmin}
        rows={bucketRows}
        onChange={setBucketRows}
        onSave={() => updateBuckets.mutate({ buckets: bucketRows })}
        isPending={updateBuckets.isPending}
        isSuccess={updateBuckets.isSuccess}
        error={updateBuckets.isError ? updateBuckets.error : null}
        isDefault={bucketsQuery.data?.buckets?.[0]?.isDefault ?? false}
      />

      {/* ── 8. Matrix segmentations (Phase 4.1.SEG) ────────────────────────── */}
      <SegmentationsCard />

      {/* ── 9. GAP configurations (Phase 4.3.GAPCONF) ───────────────────────── */}
      <GapConfigurationsCard />

      {/* ── 10. Report configurations (Phase 4.2.REPCONF) ────────────────── */}
      <ReportConfigsCard />

      {/* ── 11. Per-currency curve assignments ────────────────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Per-currency curve assignment</CardTitle>
          <CardDescription>
            One curve per currency. Cashflows in <code>currency</code> are repriced using
            the assigned <code>curveId</code>. Save this map separately from the parameters
            row above.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {form.curveAssignments.length === 0 ? (
              <p className="rounded border bg-muted/20 p-3 text-sm text-muted-foreground">
                No assignments yet. Add one (e.g. <code>MAD</code> → <code>MADBDT</code>) so
                the engines know which curve to use for each cashflow currency.
              </p>
            ) : null}
            {form.curveAssignments.map((a, i) => (
              <div key={i} className="grid items-end gap-2 md:grid-cols-[8rem_1fr_auto]">
                <Field label={i === 0 ? 'Currency' : ''} htmlFor={`a-ccy-${i}`}>
                  <Input
                    id={`a-ccy-${i}`}
                    placeholder="MAD"
                    maxLength={3}
                    disabled={!isAdmin}
                    value={a.currency}
                    onChange={(e) =>
                      patchAssignment(i, { currency: e.target.value.toUpperCase() })
                    }
                  />
                </Field>
                <Field label={i === 0 ? 'Curve id' : ''} htmlFor={`a-curve-${i}`}>
                  <Input
                    id={`a-curve-${i}`}
                    placeholder="MADBDT"
                    disabled={!isAdmin}
                    value={a.curveId}
                    onChange={(e) => patchAssignment(i, { curveId: e.target.value })}
                  />
                </Field>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  disabled={!isAdmin}
                  onClick={() => removeAssignment(i)}
                  title="Remove"
                >
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              </div>
            ))}
            <div className="flex items-center justify-between pt-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={!isAdmin}
                onClick={addAssignment}
              >
                <Plus className="mr-1 h-4 w-4" /> Add assignment
              </Button>
              <Button
                type="button"
                size="sm"
                disabled={!isAdmin || updateCurves.isPending}
                onClick={handleSaveCurves}
              >
                {updateCurves.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Save className="mr-2 h-4 w-4" />
                )}
                Save assignments
              </Button>
            </div>
            {updateCurves.isError ? <ApiErrorBanner error={updateCurves.error} /> : null}
            {updateCurves.isSuccess ? (
              <p className="flex items-center gap-1.5 text-sm text-emerald-600">
                <CheckCircle2 className="h-4 w-4" /> Curve assignments saved.
              </p>
            ) : null}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ── BucketGridCard ──────────────────────────────────────────────────────────

function BucketGridCard({
  isAdmin,
  rows,
  onChange,
  onSave,
  isPending,
  isSuccess,
  error,
  isDefault,
}: {
  isAdmin:    boolean;
  rows:       TenantBucket[];
  onChange:   (rows: TenantBucket[]) => void;
  onSave:     () => void;
  isPending:  boolean;
  isSuccess:  boolean;
  error:      CubixApiError | null;
  isDefault:  boolean;
}) {
  function patchRow(idx: number, patch: Partial<TenantBucket>) {
    const next = rows.map((r, i) => i === idx ? { ...r, ...patch } : r);
    onChange(next);
  }

  function addRow() {
    const last  = rows[rows.length - 1];
    // Close the current last bucket before appending
    const prev  = last ? { ...last, daysMax: last.daysMin + 30 } : null;
    const newRow: TenantBucket = {
      index:   rows.length,
      label:   '',
      daysMin: prev ? prev.daysMax + 1 : 0,
      daysMax: 2147483647,
    };
    const updated = prev
      ? [...rows.slice(0, -1), prev, newRow]
      : [...rows, newRow];
    // Re-index
    onChange(updated.map((r, i) => ({ ...r, index: i })));
  }

  function removeRow(idx: number) {
    if (rows.length <= 1) return;
    const next = rows.filter((_, i) => i !== idx).map((r, i) => ({ ...r, index: i }));
    // Ensure the new last bucket is open-ended
    if (next.length > 0) next[next.length - 1] = { ...next[next.length - 1], daysMax: 2147483647 };
    onChange(next);
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-4">
          <div>
            <CardTitle className="text-base">Time Bucket Grid</CardTitle>
            <CardDescription>
              Defines the time bands used by Gap Analysis to group cashflows.
              Changes take effect on the next Gap calculation — existing results
              used the previous grid and must be re-run to reflect this layout.
            </CardDescription>
          </div>
          {isDefault && (
            <span className="shrink-0 rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-700 dark:bg-blue-900 dark:text-blue-200">
              Basel defaults
            </span>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Grid table */}
        <div className="overflow-x-auto rounded-md border">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/40 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                <th className="px-3 py-2 text-left w-10">#</th>
                <th className="px-3 py-2 text-left">Label</th>
                <th className="px-3 py-2 text-right">From (days)</th>
                <th className="px-3 py-2 text-right">To (days)</th>
                <th className="px-3 py-2 w-8" />
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr key={i} className="border-b last:border-0">
                  <td className="px-3 py-2 text-muted-foreground text-xs">{i}</td>
                  <td className="px-3 py-2">
                    <Input
                      value={row.label}
                      disabled={!isAdmin}
                      placeholder="e.g. 0-1M"
                      className="h-7 w-28 text-xs"
                      onChange={(e) => patchRow(i, { label: e.target.value })}
                    />
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Input
                      type="number"
                      value={row.daysMin}
                      disabled={!isAdmin}
                      min={0}
                      className="h-7 w-24 text-right text-xs"
                      onChange={(e) => patchRow(i, { daysMin: Number(e.target.value) || 0 })}
                    />
                  </td>
                  <td className="px-3 py-2 text-right">
                    {i === rows.length - 1 ? (
                      <span className="font-mono text-xs text-muted-foreground">∞ (open)</span>
                    ) : (
                      <Input
                        type="number"
                        value={row.daysMax}
                        disabled={!isAdmin}
                        min={row.daysMin + 1}
                        className="h-7 w-24 text-right text-xs"
                        onChange={(e) => patchRow(i, { daysMax: Number(e.target.value) || row.daysMin + 1 })}
                      />
                    )}
                  </td>
                  <td className="px-3 py-2">
                    {isAdmin && rows.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeRow(i)}
                        className="text-muted-foreground hover:text-destructive transition-colors"
                        title="Remove bucket"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {isAdmin && (
          <Button type="button" variant="outline" size="sm" onClick={addRow}>
            <Plus className="mr-1 h-3.5 w-3.5" /> Add bucket
          </Button>
        )}

        {/* Warning banner — shown after a successful save */}
        {isSuccess && (
          <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <p>
              <strong>Bucket grid saved.</strong> Existing Gap Analysis results were
              computed using the previous grid. Re-run Gap Analysis on the{' '}
              <em>Gap Analysis</em> page to apply the new layout.
            </p>
          </div>
        )}

        {error ? <ApiErrorBanner error={error} /> : null}

        <div className="flex items-center gap-2">
          <Button
            type="button"
            size="sm"
            disabled={!isAdmin || isPending}
            onClick={onSave}
          >
            {isPending
              ? <><Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> Saving…</>
              : <><Save className="mr-1.5 h-3.5 w-3.5" /> Save bucket grid</>
            }
          </Button>
          <span className="text-xs text-muted-foreground">
            {rows.length} bucket{rows.length !== 1 ? 's' : ''}
          </span>
        </div>
      </CardContent>
    </Card>
  );
}

// ── Shared helpers ──────────────────────────────────────────────────────────

const selectCls =
  'flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:opacity-50';

function Field({
  label,
  htmlFor,
  hint,
  children,
}: {
  label: string;
  htmlFor: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      {label ? <Label htmlFor={htmlFor}>{label}</Label> : <span className="block h-5" />}
      {children}
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}
